'use client';

/**
 * SupportChatWidget — floating bottom-right chat bubble for /admin.
 *
 * F-500 pattern (Crisp/Intercom/Plain-style):
 *   - Collapsed: 56×56 bubble with optional unread-count badge
 *   - Expanded: 380×560 panel with message-list + composer
 *   - Realtime: Supabase channel subscribes to support_messages.INSERT
 *     filtered by conversation_id → no polling, sub-second latency
 *   - Optimistic send: tenant message renders instantly with `sending`
 *     state; server-confirmed id swaps in on POST response
 *   - Mark-as-read on expand: clears unread badge + server-side counters
 *
 * Accessibility:
 *   - Esc closes the panel
 *   - Trigger button has aria-label + role=button
 *   - Message-list has aria-live=polite so screen-readers announce
 *     incoming support replies
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, Send, Loader2, CircleHelp } from 'lucide-react';
import { useTranslations, useLocale } from 'next-intl';
import { supabase } from '@/lib/supabase/client';
import { useActiveTenant } from '@/hooks/useActiveTenant';
import { linkify } from '@/lib/support/linkify';

/** Opportunistic PostHog tracking — never throws, never blocks. */
function track(event: string, props?: Record<string, unknown>): void {
  if (typeof window === 'undefined') return;
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).posthog?.capture?.(event, props);
  } catch {
    /* opportunistic */
  }
}

interface Message {
  id: string;
  sender: 'tenant' | 'support' | 'system';
  body: string;
  created_at: string;
  /** Optimistic-only flag — message exists locally but POST hasn't resolved yet. */
  pending?: boolean;
  failed?: boolean;
}

const M3_EASE: [number, number, number, number] = [0.2, 0, 0, 1];

export function SupportChatWidget() {
  const { tenantId, name: tenantName } = useActiveTenant();
  const t = useTranslations('admin.supportChat');
  const locale = useLocale();
  const [open, setOpen] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [unread, setUnread] = useState(0);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Initial fetch — load conversation + last 50 messages.
  const hydrate = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    setError(null);
    try {
      const r = await fetch(
        `/api/support/conversation?tenantId=${tenantId}`,
        { credentials: 'same-origin' },
      );
      const d = await r.json();
      if (!r.ok || !d.success) {
        throw new Error(d.error?.message || 'Lade-Fehler');
      }
      setConversationId(d.conversation.id);
      setUnread(d.conversation.unread_for_tenant ?? 0);
      setMessages(
        (d.messages ?? []).map((m: Message) => ({
          ...m,
          pending: false,
          failed: false,
        })),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unbekannter Fehler');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    hydrate();
  }, [hydrate]);

  // iOS-Safari + PWA suspend WebSockets aggressively when the tab is
  // backgrounded. Supabase Realtime doesn't always auto-reconnect cleanly
  // after `visibilitychange → visible`, so a tenant who opens the tab
  // again sees the chat with stale messages. Force a re-hydrate on
  // visibility-change to catch what the suspended channel missed.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        void hydrate();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [hydrate]);

  // Realtime subscription — every INSERT on support_messages filtered
  // by our conversation_id streams in. RLS guarantees we only see our
  // tenant's rows even on the anon channel.
  useEffect(() => {
    if (!conversationId) return;
    const channel = supabase
      .channel(`support-chat-${conversationId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'support_messages',
          filter: `conversation_id=eq.${conversationId}`,
        },
        (payload) => {
          const m = payload.new as Message;
          setMessages((prev) => {
            // Drop optimistic placeholder if body+sender match (own send).
            const withoutPlaceholder = prev.filter(
              (x) =>
                !(
                  x.pending &&
                  x.sender === m.sender &&
                  x.body === m.body
                ),
            );
            // De-dupe by id (Realtime can fire twice during reconnect).
            if (withoutPlaceholder.some((x) => x.id === m.id)) {
              return withoutPlaceholder;
            }
            return [...withoutPlaceholder, { ...m, pending: false }];
          });
          // Support-replies bump unread (only when widget closed).
          if (m.sender === 'support' && !open) {
            setUnread((u) => u + 1);
          }
        },
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, open]);

  // Auto-scroll to bottom on new messages / open.
  useEffect(() => {
    if (open && listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages, open]);

  // On open: mark-as-read + funnel-event.
  useEffect(() => {
    if (!open || !conversationId) return;
    track('support_widget_opened', {
      conversation_id: conversationId,
      unread_count: unread,
      message_count: messages.length,
    });
    if (unread === 0) return;
    void fetch('/api/support/conversation', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ conversationId }),
    });
    setUnread(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, conversationId]);

  // Funnel event for incoming support reply when widget is OPEN (different
  // signal than when closed — open=engaged, closed=needs-badge-bump).
  useEffect(() => {
    const last = messages[messages.length - 1];
    if (!last || last.sender !== 'support') return;
    if (!open) return;
    track('support_reply_received', {
      conversation_id: conversationId,
      message_id: last.id,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages.length]);

  // Esc-to-close
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  // Focus textarea on open
  useEffect(() => {
    if (open) {
      const tid = setTimeout(() => textareaRef.current?.focus(), 200);
      return () => clearTimeout(tid);
    }
  }, [open]);

  async function send() {
    const body = input.trim();
    if (!body || !tenantId || sending) return;
    setSending(true);
    setError(null);

    // Optimistic placeholder
    const tempId = `tmp-${Date.now()}`;
    setMessages((prev) => [
      ...prev,
      {
        id: tempId,
        sender: 'tenant',
        body,
        created_at: new Date().toISOString(),
        pending: true,
      },
    ]);
    setInput('');

    try {
      const r = await fetch('/api/support/message', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ tenantId, body }),
      });
      const d = await r.json();
      if (!r.ok || !d.success) {
        // Surface rate-limit specifically — the message in d.error is
        // already user-friendly + localized in DE from the backend.
        if (r.status === 429) {
          track('support_message_rate_limited', {
            conversation_id: conversationId,
            reason: d.error?.code,
          });
        }
        throw new Error(d.error?.message || t('errors.sendFailed'));
      }
      track('support_message_sent', {
        conversation_id: conversationId,
        message_length: body.length,
      });
      // Server-confirmed message will arrive via Realtime; we just clear
      // the placeholder. (Realtime handler already does that on match.)
      // Fallback: if Realtime isn't fast enough, swap directly.
      setMessages((prev) =>
        prev.map((m) =>
          m.id === tempId
            ? {
                ...d.message,
                pending: false,
              }
            : m,
        ),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Senden fehlgeschlagen');
      setMessages((prev) =>
        prev.map((m) => (m.id === tempId ? { ...m, failed: true, pending: false } : m)),
      );
    } finally {
      setSending(false);
    }
  }

  const dateFmt = useMemo(
    () =>
      new Intl.DateTimeFormat(locale, {
        hour: '2-digit',
        minute: '2-digit',
      }),
    [locale],
  );

  // Don't render at all if there's no active tenant context yet.
  if (!tenantId) return null;

  return (
    <>
      {/* Floating trigger */}
      <AnimatePresence>
        {!open && (
          <motion.button
            key="trigger"
            type="button"
            onClick={() => setOpen(true)}
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            transition={{ duration: 0.32, ease: M3_EASE }}
            className="fixed bottom-5 right-5 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-blue-600 text-white shadow-[0_8px_24px_-6px_rgba(37,99,235,0.45)] transition-colors hover:bg-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-300 focus-visible:ring-offset-2"
            aria-label={unread > 0 ? t('triggerAriaUnread', { count: unread }) : t('triggerAria')}
          >
            <MessageCircle className="h-6 w-6" strokeWidth={2} />
            {unread > 0 && (
              <span className="absolute -right-1 -top-1 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-rose-500 px-1.5 text-[11px] font-bold text-white shadow ring-2 ring-white">
                {unread > 9 ? '9+' : unread}
              </span>
            )}
          </motion.button>
        )}
      </AnimatePresence>

      {/* Expanded panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            key="panel"
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.32, ease: M3_EASE }}
            className="fixed bottom-5 right-5 z-50 flex h-[560px] w-[380px] max-w-[calc(100vw-2.5rem)] flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-[0_24px_64px_-16px_rgba(15,23,42,0.35)]"
            role="dialog"
            aria-label={t('dialogAria')}
          >
            {/* Header */}
            <header className="flex items-center justify-between border-b border-gray-100 bg-gradient-to-br from-blue-600 to-blue-700 px-4 py-3 text-white">
              <div className="flex items-center gap-2.5">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/15">
                  <CircleHelp className="h-4 w-4" strokeWidth={2.25} />
                </div>
                <div>
                  <div className="text-sm font-semibold leading-tight">
                    {t('headerTitle')}
                  </div>
                  <div className="text-[11px] font-medium text-blue-100">
                    {t('headerSubtitle')}
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="rounded-md p-1.5 text-white/80 transition-colors hover:bg-white/10 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
                aria-label={t('closeAria')}
              >
                <X className="h-4 w-4" strokeWidth={2.25} />
              </button>
            </header>

            {/* Message list */}
            <div
              ref={listRef}
              className="flex-1 space-y-3 overflow-y-auto bg-gray-50/40 px-3 py-3"
              aria-live="polite"
            >
              {loading && messages.length === 0 && (
                <div className="flex h-full items-center justify-center">
                  <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                </div>
              )}
              {!loading && messages.length === 0 && (
                <div className="px-3 py-6 text-center text-sm text-gray-500">
                  <p className="font-medium text-gray-700">
                    👋 {t('emptyGreeting', { name: tenantName ?? t('emptyGreetingFallback') })}
                  </p>
                  <p className="mt-2">{t('emptyBody')}</p>
                  <p className="mt-2 text-xs text-gray-400">{t('emptyFooter')}</p>
                </div>
              )}
              {messages.map((m) => (
                <Bubble
                  key={m.id}
                  m={m}
                  dateFmt={dateFmt}
                  pendingLabel={t('bubble.sending')}
                  failedLabel={t('bubble.failed')}
                />
              ))}
            </div>

            {/* Composer */}
            <div className="border-t border-gray-100 bg-white p-3">
              {error && (
                <div className="mb-2 rounded-md border border-rose-200 bg-rose-50 px-2 py-1 text-xs text-rose-700">
                  {error}
                </div>
              )}
              <div className="flex items-end gap-2">
                <textarea
                  ref={textareaRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      void send();
                    }
                  }}
                  placeholder={t('composerPlaceholder')}
                  rows={2}
                  className="flex-1 resize-none rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
                  disabled={sending}
                  maxLength={4000}
                />
                <button
                  type="button"
                  onClick={() => void send()}
                  disabled={sending || !input.trim()}
                  className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-600 text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-400"
                  aria-label={t('sendAria')}
                >
                  {sending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4" strokeWidth={2.25} />
                  )}
                </button>
              </div>
              <p className="mt-1.5 text-[10px] text-gray-400">{t('shortcuts')}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

function Bubble({
  m,
  dateFmt,
  pendingLabel,
  failedLabel,
}: {
  m: Message;
  dateFmt: Intl.DateTimeFormat;
  pendingLabel: string;
  failedLabel: string;
}) {
  const isTenant = m.sender === 'tenant';
  const isSystem = m.sender === 'system';
  if (isSystem) {
    return (
      <div className="mx-auto max-w-[80%] rounded-full bg-gray-100 px-3 py-1 text-center text-[11px] text-gray-500">
        {m.body}
      </div>
    );
  }
  const tokens = linkify(m.body);
  const linkClass = isTenant
    ? 'underline decoration-blue-200 underline-offset-2 hover:text-white hover:decoration-white'
    : 'text-blue-600 underline decoration-blue-200 underline-offset-2 hover:decoration-blue-500';
  return (
    <div className={`flex ${isTenant ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-[85%] rounded-2xl px-3.5 py-2 text-sm leading-snug whitespace-pre-wrap break-words ${
          isTenant
            ? 'rounded-br-md bg-blue-600 text-white'
            : 'rounded-bl-md bg-white text-gray-900 ring-1 ring-gray-200'
        } ${m.pending ? 'opacity-70' : ''} ${m.failed ? 'ring-rose-300' : ''}`}
      >
        <div>
          {tokens.map((tok, i) =>
            tok.type === 'link' ? (
              <a
                key={i}
                href={tok.value}
                target="_blank"
                rel="noopener noreferrer"
                className={linkClass}
              >
                {tok.value}
              </a>
            ) : (
              <span key={i}>{tok.value}</span>
            ),
          )}
        </div>
        <div
          className={`mt-0.5 text-[10px] tabular-nums ${
            isTenant ? 'text-blue-100' : 'text-gray-400'
          }`}
        >
          {dateFmt.format(new Date(m.created_at))}
          {m.pending && ` · ${pendingLabel}`}
          {m.failed && ` · ${failedLabel}`}
        </div>
      </div>
    </div>
  );
}
